import React, { useState, useEffect } from "react";
import {
  IonContent,
  IonPage,
  IonItem,
  IonText,
  useIonRouter,
  IonInput,
  IonToast,
  IonIcon,
  IonModal,
  IonButton,
} from "@ionic/react";
import "./Settings.css"; // Assuming you have a CSS file for styles
import Header from "../../components/Header";
import { UserProfileRaw } from "../../contexts/types";
import {
  pencil,
  image,
  calendarOutline,
  personCircleOutline,
} from "ionicons/icons";
import useAuth from "../../hooks/useAuth";
import SelectionModal from "../../components/ui/popups/SelectionModal";
import { Diagnosis, getProfileById, updateProfile } from "../../services/user";
import { isCustomAvatar } from "./AvatarSelection";
import avatarsSprite from "../../assets/icons/avatars.svg";
import InitialsAvatar from "../../components/ui/InitialsAvatar";
import { getAllDiagnoses } from "../../services/user";
import DatePickerWheel from "../../components/ui/Sliders/DatePickerWheel";
import OtpPopup from "../../components/ui/popups/OtpPopup";
import { countryCodes } from "../../utils/constants";
import ActionButtons from "../../components/ui/popups/ActionButtons";
import AuthenticatedImage from "../../components/journal/AuthenticatedImage";
import { formatOwnAddress } from "./ownAddress";
interface FormData {
  profile_id: number;
  first_name?: string;
  last_name?: string;
  phone: string;
  country_code: string;
  date_of_birth?: string;
  email?: string;
  avatar?: string;
  gender?: string;
  existing_conditions?: string[];
}



const MyProfilePage: React.FC = () => {
  // State to store data (simulating data from server)
  const [profile, setProfile] = useState({});
  // B2-4 / D51 — the patient's OWN address (`profile.address`).
  // ⛔ DELIBERATELY NOT PART OF `formData`: `formData` is the EDITABLE form, and it is spread
  // wholesale into `updateUserProfile` and into blaze's `PUT /user/updateProfile`. The address
  // has no writer on either backend (see `ownAddress.ts`), so keeping it out of the form is what
  // stops a later edit from being posted into a route that silently drops it.
  // ⛔ `unknown`, not `Address`, and deliberately so: this arrives straight off the wire from a
  // JSON column three applications write, so its shape is a claim rather than a fact. Typing it
  // `Address` would let a reader dereference `.city` on a string, and App.tsx's null-returning
  // ErrorBoundary turns any such throw into a BLANK APP (TD-165). `formatOwnAddress` is the one
  // place that proves the shape.
  const [ownAddress, setOwnAddress] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  // Form data state using a single object
  const [formData, setFormData] = useState<FormData>({
    profile_id: 0,
    first_name: "",
    last_name: "",
    phone: "",
    date_of_birth: "",
    email: "",
    avatar: "",
    country_code: "",
  });
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showGenderSelection, setShowGenderSelection] = useState(false);
  const [showDateofBirthSelection, setShowDateofBirthSelection] =
    useState(false);
  const [showExisitingConditions, setShowExisitingConditions] = useState(false);
  const [showAvatarSelector, setShowAvatarSelector] = useState(false);
  const [showProfilePicChange, setShowProfilePicChange] = useState(false);
  const { user, updateUserProfile } = useAuth();
  const [showDateSelector, setShowDateSelector] = useState(false);

  const [days, setDays] = useState<number>(0);
  const [months, setMonths] = useState<number>(0);
  const [years, setYears] = useState<number>(0);
  const [emailOtpPopup, setEmailOtpPopup] = useState(false);
  const [phoneOtpPopup, setPhoneOtpPopup] = useState(false);
  const [dateFormat, setDateFormat] = useState<string>("");

  const getDaysInMonth = (monthIndex: number, yearIndex: number) => {
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 100 }, (_, i) => currentYear - 105 + i);
    const actualYear = years[yearIndex];
    const actualMonth = monthIndex; // 0-based month
    return new Date(actualYear, actualMonth + 1, 0).getDate();
  };
  const [countryCode, setCountryCode] = useState(
    `${user?.phone_country_code ? user.phone_country_code : "+1"}`
  );
  const daysArray = getDaysInMonth(months, years);
  const dayArray = Array.from({ length: daysArray }, (_, i) =>
    (i + 1).toString().padStart(2, "0")
  );

  // // Arrays for the wheels
  // const daysArray = Array.from({ length: 31 }, (_, i) =>
  //   (i + 1).toString().padStart(2, "0")
  // );
  const monthsArray = Array.from({ length: 12 }, (_, i) =>
    (i + 1).toString().padStart(2, "0")
  );
  const currentYear = new Date().getFullYear();
  const yearsArray = Array.from({ length: 101 }, (_, i) =>
    (currentYear - i).toString()
  );

  const router = useIonRouter();
  const [diagnosisOptions, setDiagnosisOptions] = useState<Diagnosis[]>([]);
  useEffect(() => {
    setCountryCode(formData.country_code || "+1");
  }, [formData.country_code]);
  
  useEffect(() => {
    const fetchProfile = async () => {
      if (isInitialized) return;

      const { profile: profileData }: { profile: UserProfileRaw } =
        await getProfileById();

      setProfile(profileData);
      setOwnAddress(profileData.address);
      setFormData({
        profile_id: profileData.id,
        date_of_birth: profileData.dob || "",
        country_code: profileData.phone_country_code || "+1",
        gender: profileData.condition?.gender || undefined,
        existing_conditions: profileData.condition?.existing_conditions || [],
        ...profileData,
      });

      setIsLoading(false);
      setIsInitialized(true);
    };
    fetchProfile();
  }, [isInitialized]);

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  console.log("Detected Time Zone:", timeZone);
  useEffect(() => {
    const setDateFormatByLocation = async () => {
      if (timeZone) {
        try {
          console.log(
            countryCodes.find((country) => country.timeZone === timeZone)
              ?.dateFormat
          );
          const dateFormat =
            countryCodes.find((country) => country.timeZone === timeZone)
              ?.dateFormat || "MM/DD/YYYY";
          setDateFormat(dateFormat);
        } catch {
          // fallback if parsing fails
          const dateFormat =
            countryCodes.find((country) => country.timeZone === timeZone)
              ?.dateFormat || "MM/DD/YYYY";
          setDateFormat(dateFormat);
        }
      } else {
        const CountryCode = formData.country_code || "US";
        const dateFormat =
          countryCodes.find((country) => country.code === CountryCode)
            ?.dateFormat || "MM/DD/YYYY";
        setDateFormat(dateFormat);
      }
    };
    setDateFormatByLocation();
  }, [timeZone]);

  useEffect(() => {
    if (formData.date_of_birth) {
      // Parse the date_of_birth (expected format: YYYY-MM-DD)
      const [year, month, day] = formData.date_of_birth
        .split("T")[0]
        .split("-");
      // Find the index in yearsArray, monthsArray, dayArray
      const yearIndex = yearsArray.findIndex((y) => y === year);
      const monthIndex = monthsArray.findIndex((m) => m === month);
      const dayIndex = dayArray.findIndex((d) => d === day);

      setYears(yearIndex >= 0 ? yearIndex : 0);
      setMonths(monthIndex >= 0 ? monthIndex : 0);
      setDays(dayIndex >= 0 ? dayIndex : 0);
    } else {
      setDays(0);
      setMonths(0);
      setYears(0);
    }
  }, [formData.date_of_birth]);

  useEffect(() => {
    const fetchDiagnoses = async () => {
      try {
        const response = await getAllDiagnoses();
        setDiagnosisOptions(response.diagnoses || []);
      } catch (error) {
        setDiagnosisOptions([]);
      }
    };
    fetchDiagnoses();
  }, []);

  const handleBack = () => {
    updateUserProfile({
      ...formData,
      existing_conditions: formData.existing_conditions || [],
    });
    router.push("/settings/account", "back");
  };

  const CloseModal = () => {
    setEmailOtpPopup(false);
    setPhoneOtpPopup(false);
  };

  const handleFormChange = (e: CustomEvent, field: keyof FormData) => {
    let selectedValues: string[] = [];
    // check if the value is same as previous
    const isSameAsPrevious = (value: string[]) => {
      return JSON.stringify(value) === JSON.stringify(formData[field]);
    };

    // Handle different types of form changes
    if (field === "existing_conditions") {
      // Ensure proper array handling for existing_conditions
      if (Array.isArray(e.detail.value)) {
        selectedValues = e.detail.value;
      } else if (typeof e.detail.value === "string") {
        // If it's a single string, treat as single selection or comma-separated
        selectedValues = e.detail.value
          .split("|")
          .map((item: any) => item.trim())
          .filter(Boolean);
      } else if (e.detail.value) {
        selectedValues = [e.detail.value];
      } else {
        selectedValues = [];
      }

      setFormData((prev) => ({
        ...prev,
        [field]: selectedValues,
      }));
    } else {
      // Handle all other field types
      let value = e.detail?.value ?? "";
      // Auto-capitalize first letter for first_name and last_name
      if (field === "first_name" || field === "last_name") {
        if (typeof value === "string" && value.length > 0) {
          value = value.charAt(0).toUpperCase() + value.slice(1);
        }
      }
      if (!isSameAsPrevious(value)) {
        setFormData((prev) => ({
          ...prev,
          [field]: value,
        }));
      }
    }

    // Save specific fields immediately
    if (
      field === "existing_conditions" ||
      field === "gender" ||
      field === "avatar"
    ) {
      const updatedProfile = {
        [field]:
          field === "existing_conditions"
            ? Array.isArray(e.detail.value)
              ? selectedValues
              : [...selectedValues]
            : e.detail.value,
      };
      if (!isSameAsPrevious(selectedValues)) {
        setTimeout(() => {
          handleSaveProfile(updatedProfile);
        }, 500);
      }
    }
  };

  const handleSaveProfile = async (updatedProfileData?: any) => {
    // Update profile in state
    // Add condition to check if the value is same as previous
    if (profile) {
      const updatedProfile = {
        ...(updatedProfileData ? updatedProfileData : formData),
      };
      try {
        const updatedUser: any = await updateProfile(updatedProfile);

        updateUserProfile({
          ...updatedUser.user,
          ...updatedUser.user.condition,
        } as any);
        // showToastMessage("Profile updated successfully");
      } catch (error: any) {
        console.error("Error updating profile:", error);
        showToastMessage(
          (error && error?.message) || "Failed to update profile"
        );
      }
    }
  };

  const showToastMessage = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
  };

  // Handle avatar selection
  const handleAvatarSelect = async (avatarId: string) => {
    // Update the form data
    setFormData((prev) => ({
      ...prev,
      avatar: avatarId,
    }));
  };
  const sanitize = (str: string) => str.replace(/[()\s_-]/g, "");

  const PhoneNum = user?.phone
    ? sanitize(user.phone).startsWith(sanitize(formData.country_code))
      ? sanitize(user.phone).slice(sanitize(formData.country_code).length)
      : sanitize(user.phone)
    : undefined;

  // B2-4 / D51 — the patient's own address, proven into display lines by the one module that
  // guards the shape. ⚠️ Guarded here rather than at the render site because `profile.address`
  // is a JSON column three applications write: `undefined`, `null`, `{}` and a bare string all
  // occur, and App.tsx's ErrorBoundary returns null, so one unguarded `.city` BLANKS THE WHOLE
  // APP (TD-165, and the `getDisplayName` defect fixed on adfd422).
  const addressLines = formatOwnAddress(ownAddress);

  const formatDateByCountry = (dateString: string): string => {
    if (!dateString) return "";

    // Extract date part (remove time if present)
    const datePart = dateString.includes("T")
      ? dateString.slice(0, 10)
      : dateString;

    // Parse the date (assuming input is YYYY-MM-DD format)
    const [year, month, day] = datePart.split("-");

    // Format according to country
    switch (dateFormat) {
      case "MM/DD/YYYY":
        return `${month}/${day}/${year}`;
      case "DD/MM/YYYY":
        return `${day}/${month}/${year}`;
      case "DD.MM.YYYY":
        return `${day}.${month}.${year}`;
      case "DD-MM-YYYY":
        return `${day}-${month}-${year}`;
      case "YYYY-MM-DD":
        return `${year}-${month}-${day}`;
      case "YYYY/MM/DD":
        return `${year}/${month}/${day}`;
      default:
        return `${day}/${month}/${year}`;
    }
  };

  // If data is still loading, you could show a spinner here
  if (isLoading) {
    return (
      <IonPage>
        <Header
          showGreeting={false}
          showPageTitle={true}
          showUserIcon={false}
          pageTitle="My Profile"
          showBackButton={true}
          onBackButtonClick={handleBack}
        />
        <IonContent className="ion-padding account-content">
          <div className="loading-container">
            <p>Loading account information...</p>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  // Once data is loaded
  return (
    <IonPage>
      {/* Header with gradient background */}
      <Header
        showGreeting={false}
        showPageTitle={true}
        showUserIcon={false}
        pageTitle="My Profile"
        showBackButton={true}
        onBackButtonClick={handleBack}
      />
      {/* Create page for this just replace the content */}
      <IonContent className="bg-white account-content">
        <div className="bg-[#F5F5F5]">
          <div className="p-4">
            <div className="relative mx-auto mb-6">
              <div className="relative mx-auto mb-6">
                {/* Main avatar container */}
                <div
                  className="relative w-[110px] h-[110px] mx-auto bg-gray-200 border border-[#05ACD9] rounded-full flex justify-center items-center overflow-hidden box-border"
                  onClick={() => {
                    setShowProfilePicChange(true);
                  }}
                >
                  {formData.avatar ? (
                    isCustomAvatar(formData.avatar) ? (
                      // For uploaded image
                      // <img
                      //   src={formData.avatar}
                      //   alt="User Avatar"
                      //   className="w-full h-full object-cover"
                      //   onError={(e) => {
                      //     e.currentTarget.style.display = "none";
                      //     // Show initials avatar as fallback
                      //   }}
                      // />
                      <AuthenticatedImage
                        src={formData.avatar}
                        alt="User Avatar"
                        className="w-full h-full object-cover"
                      // images={post.images}
                      // index={index}
                      />
                    ) : (
                      // For SVG avatar
                      <div className="w-full h-full flex items-center justify-center">
                        <svg
                          className="w-[120%] h-[120%]"
                          viewBox="0 0 200 200"
                        >
                          <use href={`${avatarsSprite}#${formData.avatar}`} />
                        </svg>
                      </div>
                    )
                  ) : formData.first_name || formData.last_name ? (
                    <InitialsAvatar
                      firstName={formData.first_name}
                      lastName={formData.last_name}
                      size={110}
                      className="border-2 border-blue-500"
                    />
                  ) : (
                    <IonIcon
                      icon={personCircleOutline}
                      style={{ width: "150px", height: "150px" }}
                      className="text-gray-400"
                    />
                  )}
                </div>

                {/* Gallery Icon - Positioned outside the avatar container */}
                <div
                  className="absolute w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md cursor-pointer border border-[#05ACD9]"
                  style={{ bottom: "0", right: "calc(50% - 55px)" }}
                  onClick={() => {
                    setShowProfilePicChange(true);
                  }}
                >
                  {formData.avatar ? (
                    <IonIcon
                      icon={pencil}
                      className="text-[#05ACD9]"
                      style={{
                        width: "16px",
                        height: "16px",
                        color: "#05ACD9",
                      }}
                    />
                  ) : (
                    <IonIcon icon={image} className="text-[#05ACD9]" />
                  )}
                </div>
              </div>
            </div>

            {/* First Name */}
            <div className="mb-6">
              <IonText className="text-gray-600 text-base font-medium pl-1">
                First Name
              </IonText>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                <IonItem className="rounded-lg mt-1 bg-white" lines="none">
                  <IonInput
                    value={formData.first_name}
                    maxlength={40}
                    onIonInput={(e) => {
                      handleFormChange(e as CustomEvent, "first_name");
                    }}
                    onIonBlur={() => {
                      if (
                        typeof formData.first_name === "string" &&
                        formData.first_name.trim() === ""
                      ) {
                        // setFormData((prev) => ({
                        //   ...prev,
                        //   first_name: user?.first_name || "",
                        // }));
                      } else {
                        //field name and value
                        handleSaveProfile({ first_name: formData.first_name });
                      }
                    }}
                    className="py-2 text-lg"
                    placeholder="First Name"
                  />
                </IonItem>
                <p
                  className="text-red-500"
                  style={{ paddingLeft: "5px", fontFamily: "Figtree" }}
                >
                  {typeof formData.first_name === "string" &&
                    formData.first_name.length >= 40 &&
                    "First name must be 40 characters or less"}
                </p>
              </div>
            </div>

            {/* Last Name */}
            <div className="mb-6">
              <IonText className="text-gray-600 text-base font-medium pl-1">
                Last Name
              </IonText>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                <IonItem className="rounded-lg mt-1 bg-white" lines="none">
                  <IonInput
                    maxlength={40}
                    value={formData.last_name}
                    onIonInput={(e) => {
                      // let value = e.detail.value || "";
                      // value = value.charAt(0).toUpperCase() + value.slice(1);
                      handleFormChange(e as CustomEvent, "last_name");
                    }}
                    onIonBlur={() => {
                      // If the field is empty, revert to previous value (from user context)
                      if (
                        typeof formData.last_name === "string" &&
                        formData.last_name.trim() === ""
                      ) {
                        // setFormData((prev) => ({
                        //   ...prev,
                        //   last_name: user?.last_name || "",
                        // }));
                      } else {
                        handleSaveProfile({ last_name: formData.last_name });
                      }
                    }}
                    className="py-2 text-lg"
                    placeholder="Last Name"
                  />
                </IonItem>
                <p
                  className="text-red-500"
                  style={{ paddingLeft: "5px", fontFamily: "Figtree" }}
                >
                  {typeof formData.last_name === "string" &&
                    formData.last_name.length >= 40 &&
                    "Last name must be 40 characters or less"}
                </p>
              </div>
            </div>

            {/* Email */}
            <div className="mb-6">
              <IonText className="text-gray-600 text-base font-medium pl-1">
                Email
              </IonText>
              <IonItem
                className="rounded-lg mt-1 bg-white"
                lines="none"
                onClick={() => {
                  setEmailOtpPopup(true);
                }}
              >
                <div
                  onClick={() => {
                    setEmailOtpPopup(true);
                  }}
                  className="py-2 text-lg"
                  style={{
                    color: !formData.email ? "#999" : undefined,
                  }}
                >
                  {formData.email || "Add Email"}
                </div>
              </IonItem>
            </div>
            {/* Phone */}
            <div className="mb-6">
              <IonText className="text-gray-600 text-base font-medium pl-1">
                Phone
              </IonText>
              <IonItem className="rounded-lg mt-1 bg-white" lines="none">
                <div
                  onClick={() => {
                    setPhoneOtpPopup(true);
                  }}
                  className="py-2 text-lg"
                  style={{
                    width: "100%",
                    color: !formData.phone ? "#999" : undefined,
                  }}
                >
                  {formData.phone || "Phone Number"}
                </div>
              </IonItem>
            </div>

            {/* Address — B2-4 / D51. THE PATIENT'S OWN COPY (`profile.address`), read-only.
                ⛔ Read-only is a BACKEND FACT, not a UI preference: neither backend accepts an
                address write today. The kernel's `PATCH /me` takes no `address` (its
                `ProfileUpdateRequest` is names + date_of_birth + time_zone + user_name, and
                B2-4 declined to add one on purpose), and blaze's `PUT /user/updateProfile` —
                this screen's own writer, via `handleSaveProfile` — omits `address` from its
                `basicFields` and SILENTLY DROPS it while still answering 200. An input here
                would show the patient a save that persists nothing, so there is deliberately no
                edit affordance: no chevron, no pencil, no onClick. See `ownAddress.ts`.
                ⛔ And it is never the ORGANIZATION's copy — ACDPA-196 c.13178. */}
            <div className="mb-6">
              <IonText className="text-gray-600 text-base font-medium pl-1">
                Address
              </IonText>
              <IonItem className="rounded-lg mt-1 bg-white" lines="none">
                <div
                  className="py-2 text-lg w-full break-words"
                  style={{
                    minHeight: "40px",
                    lineHeight: "1.5",
                    color: addressLines.length > 0 ? "#000" : "#999",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "center",
                  }}
                >
                  {/* ⚠️ Keyed by index on purpose: this list is derived fresh on every
                      render, is never reordered or filtered in place, and its values are NOT
                      unique — an address_line_2 equal to address_line_1 would be a duplicate
                      key, and a React warning, if the line itself were used. */}
                  {addressLines.length > 0
                    ? addressLines.map((line, index) => (
                      <div key={index}>{line}</div>
                    ))
                    : "No address on file"}
                </div>
              </IonItem>
            </div>

            {/* Date of Birth */}
            <div className="mb-6">
              <IonText className="text-gray-600 text-base font-medium pl-1">
                Date of Birth
              </IonText>
              <IonItem className="rounded-lg mt-1 bg-white" lines="none">
                <div
                  className="flex justify-between items-center w-full"
                  onClick={() => setShowDateSelector(true)}
                >
                  <IonInput
                    value={
                      formData.date_of_birth
                        ? formatDateByCountry(formData.date_of_birth)
                        : ""
                    }
                    placeholder="Select D.O.B"
                    className="py-2 text-lg w-full !cursor-pointer"
                    readonly={true}
                  // onIonBlur={() => handleSaveProfile()}
                  />
                  <div slot="end" className="flex items-center pr-2">
                    <IonIcon
                      icon={calendarOutline}
                      style={{ fontSize: 18, color: "#777777" }}
                      slot="icon-only"
                    />
                  </div>
                </div>
                {showDateSelector && (
                  <IonModal
                    isOpen={showDateSelector}
                    onDidDismiss={() => setShowDateSelector(false)}
                    className="date-selector-modal"
                    style={{
                      padding: "12px",
                      // "--width": "95%",
                      "--height": "auto",
                      "--border-radius": "30px",
                      "--max-height": "auto",
                      "--bottom": "0",
                      "--padding": "10px",
                      "--background": "white", // Ensures modal background is white
                      // background: "white",     // Fallback for inline style
                    }}
                  >
                    <button
                      className="absolute top-4 right-4 text-gray-500"
                      onClick={() => setShowDateSelector(false)}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6 18L18 6M6 6L18 18"
                          stroke="#777777"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <div
                      className="p-6 flex flex-col"
                      style={{ background: "white" }}
                    >
                      <div
                        className="flex justify-between fa fa-align-center items-center mb-4 "
                        style={{
                          paddingTop: "20px",
                          alignContent: "center",
                          justifyContent: "center",
                        }}
                      >
                        <h1
                          className="text-black text-[24px] leading-[28px] font-bold text-center"
                          style={{
                            fontFamily: "Figtree",
                            fontStyle: "normal",
                            fontWeight: 700,
                            fontSize: "24px",
                            lineHeight: "28px",
                            textAlign: "center",
                            width: "309px",
                            height: "28px",
                            color: "#000000",
                            flex: "none",
                            order: 0,
                            flexGrow: 0,
                            zIndex: 0,
                          }}
                        >
                          Date of Birth
                        </h1>
                      </div>
                      <div
                        className="flex gap-4"
                        style={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "center",
                          fontSize: "18px",
                          gap: "35px",
                          paddingLeft: "20px",
                        }}
                      >
                        {(() => {
                          switch (dateFormat) {
                            case "MM/DD/YYYY":
                              return (
                                <>
                                  <p>MM</p>
                                  <p>DD</p>
                                  <p>YYYY</p>
                                </>
                              );
                            case "YYYY/MM/DD":
                            case "YYYY-MM-DD":
                              return (
                                <>
                                  <p>YYYY</p>
                                  <p>MM</p>
                                  <p>DD</p>
                                </>
                              );
                            default:
                              return (
                                <>
                                  <p style={{ paddingRight: "5px" }}>DD</p>
                                  <p>MM</p>
                                  <p>YYYY</p>
                                </>
                              );
                          }
                        })()}
                      </div>

                      <div
                        className="flex gap-2"
                        style={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "center",
                          fontSize: "18px",
                          fontWeight: "bold", // fixed
                        }}
                      >
                        {dateFormat === "MM/DD/YYYY" ? (
                          <>
                            <DatePickerWheel
                              items={monthsArray}
                              selectedIndex={months}
                              onSelect={setMonths}
                              label="MONTHS"
                              isActive={true}
                            />
                            <DatePickerWheel
                              items={dayArray}
                              selectedIndex={days}
                              onSelect={setDays}
                              label="DAYS"
                              isActive={true}
                            />
                            <DatePickerWheel
                              items={yearsArray}
                              selectedIndex={years}
                              onSelect={setYears}
                              label="YEARS"
                              isActive={true}
                            />
                          </>
                        ) : dateFormat === "YYYY/MM/DD" ||
                          dateFormat === "YYYY-MM-DD" ? (
                          <>
                            <DatePickerWheel
                              items={yearsArray}
                              selectedIndex={years}
                              onSelect={setYears}
                              label="YEARS"
                              isActive={true}
                            />
                            <DatePickerWheel
                              items={monthsArray}
                              selectedIndex={months}
                              onSelect={setMonths}
                              label="MONTHS"
                              isActive={true}
                            />
                            <DatePickerWheel
                              items={dayArray}
                              selectedIndex={days}
                              onSelect={setDays}
                              label="DAYS"
                              isActive={true}
                            />
                          </>
                        ) : (
                          <>
                            <DatePickerWheel
                              items={dayArray}
                              selectedIndex={days}
                              onSelect={setDays}
                              label="DAYS"
                              isActive={true}
                            />
                            <DatePickerWheel
                              items={monthsArray}
                              selectedIndex={months}
                              onSelect={setMonths}
                              label="MONTHS"
                              isActive={true}
                            />
                            <DatePickerWheel
                              items={yearsArray}
                              selectedIndex={years}
                              onSelect={setYears}
                              label="YEARS"
                              isActive={true}
                            />
                          </>
                        )}
                      </div>

                      <div
                        className="flex flex-col gap-3 justify-between mt-8"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          flexDirection: "column",
                        }}
                      >
                        <IonButton
                          expand="block"
                          onClick={() => {
                            // Compose date string in YYYY-MM-DD format for consistency
                            const selectedDay = dayArray[days];
                            const selectedMonth = monthsArray[months];
                            const selectedYear = yearsArray[years];
                            const dateString = `${selectedYear}-${selectedMonth}-${selectedDay}`; // <-- ISO format

                            setFormData((prev) => ({
                              ...prev,
                              date_of_birth: dateString,
                            }));
                            handleSaveProfile({
                              date_of_birth: dateString,
                            });
                            setShowDateSelector(false);
                          }}
                          className="primary-button w-full mb-4"
                          style={{
                            "--border-radius": "30px",
                            fontSize: "18px",
                            fontWeight: "500",
                          }}
                        >
                          Save
                        </IonButton>
                        <button
                          className="text-red-600 px-6 py-2 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              date_of_birth: "",
                            }));
                            handleSaveProfile({
                              ...formData,
                              date_of_birth: "",
                            });
                            setShowDateSelector(false);
                          }}
                          disabled={!formData.date_of_birth}
                        >
                          Remove D.O.B
                        </button>
                      </div>
                    </div>
                  </IonModal>
                )}
              </IonItem>
            </div>

            {/* Gender */}
            <div className="mb-6">
              <IonText className="text-gray-600 text-base font-medium pl-1">
                Gender
              </IonText>
              <IonItem className="rounded-lg mt-1 bg-white" lines="none">
                <div
                  className="flex justify-between items-center w-full"
                  onClick={() => setShowGenderSelection(true)}
                >
                  <IonInput
                    value={formData.gender}
                    placeholder="Select Gender"
                    className="py-2 text-lg w-full !cursor-pointer"
                    readonly={true}
                  />
                  <div slot="end" className="flex items-center pr-2">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4 6L8 10L12 6"
                        stroke="#777777"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
                {showGenderSelection && (
                  <SelectionModal
                    isOpen={showGenderSelection}
                    onClose={() => setShowGenderSelection(false)}
                    title="Gender"
                    options={[
                      {
                        id: "Male",
                        label: "Male",
                        selected: formData.gender === "Male",
                      },
                      {
                        id: "Female",
                        label: "Female",
                        selected: formData.gender === "Female",
                      },
                      {
                        id: "Non-Binary",
                        label: "Non-Binary",
                        selected: formData.gender === "Non-Binary",
                      },
                    ]}
                    fieldName="gender"
                    buttonText="Save"
                    onSave={handleFormChange as any}
                    onRemove={() => {
                      handleFormChange(
                        { detail: { value: "" } } as CustomEvent,
                        "gender"
                      );
                      setShowGenderSelection(false);
                    }}
                    multiSelect={false}
                  />
                )}
              </IonItem>
            </div>

            {/* Diagnoses/Existing Conditions */}
            <div className="mb-6" style={{ paddingBottom: "30px" }}>
              <IonText className="text-gray-600 text-base font-medium pl-1">
                Diagnoses
              </IonText>
              <IonItem className="rounded-lg mt-1 bg-white" lines="none">
                <div
                  className="flex justify-between items-center w-full"
                  onClick={() => setShowExisitingConditions(true)}
                >
                  <div
                    className="py-2 text-lg w-full cursor-pointer"
                    style={{
                      minHeight: "40px",
                      // maxHeight: "80px",
                      overflowY: "auto",
                      whiteSpace: "pre-line",
                      lineHeight: "1.5",
                      color:
                        formData.existing_conditions &&
                          formData.existing_conditions.length > 0
                          ? "#000"
                          : "#999",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      paddingTop:
                        formData.existing_conditions &&
                          formData.existing_conditions.length > 1
                          ? "8px"
                          : "0",
                    }}
                  >
                    {formData.existing_conditions &&
                      formData.existing_conditions.length > 0
                      ? formData.existing_conditions.map((condition) => (
                        <React.Fragment key={condition}>
                          {condition.trim()}
                          <br />
                        </React.Fragment>
                      ))
                      : "Diagnoses"}
                  </div>
                  <div className="flex items-center pr-2 flex-shrink-0">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4 6L8 10L12 6"
                        stroke="#777777"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
                {showExisitingConditions && (
                  <SelectionModal
                    isOpen={showExisitingConditions}
                    onClose={() => setShowExisitingConditions(false)}
                    title="Diagnoses"
                    subtitle="Select all that apply"
                    options={[
                      ...diagnosisOptions.map((diagnosis) => ({
                        id: diagnosis.description,
                        label: `${diagnosis.description} (${diagnosis.code})`,
                        selected: Array.isArray(formData?.existing_conditions)
                          ? formData.existing_conditions.includes(
                            diagnosis?.description.trim()
                          )
                          : false,
                      })),
                      // Add more options here
                    ]}
                    selectedOnTop={false}
                    showSearch={true}
                    fieldName="existing_conditions"
                    onSave={handleFormChange as any}
                    onRemove={() => {
                      handleFormChange(
                        { detail: { value: [] } } as CustomEvent,
                        "existing_conditions"
                      );
                      setShowExisitingConditions(false);
                    }}
                    buttonText="Save"
                    multiSelect={true}
                    allowCustomOptions={false}
                  />
                )}
              </IonItem>
            </div>
          </div>
        </div>
      </IonContent>
      {/* Avatar Selector Modal */}
      {/* <AvatarSelector
        isOpen={showAvatarSelector}
        onClose={() => setShowAvatarSelector(false)}
        onSelect={handleAvatarSelect}
        currentAvatar={formData.avatar}
      /> */}

      {emailOtpPopup && (
        <OtpPopup
          isOpen={emailOtpPopup}
          onClose={() => {
            CloseModal();
          }}
          phone={PhoneNum || ""}
          countryCode={countryCode}
          setCountryCode={(val: string) => setCountryCode(val)}
          setPhone={(val: string) =>
            setFormData((prev) => ({ ...prev, phone: val }))
          }
          email={formData.email || ""}
          setEmail={(val: string) =>
            setFormData((prev) => ({ ...prev, email: val }))
          }
          userPhone={formData?.phone || ""}
          userEmail={formData?.email || ""}
          userCountryCode={formData?.country_code || ""}
          handleSaveProfile={(contact: string, type: "phone" | "email") =>
            handleSaveProfile({ ...formData, [type]: contact })
          }
          showToastMessage={showToastMessage}
          mode="email"
        />
      )}
      {phoneOtpPopup && (
        <OtpPopup
          isOpen={phoneOtpPopup}
          onClose={() => {
            CloseModal();
          }}
          phone={PhoneNum || ""}
          countryCode={countryCode}
          setCountryCode={(val: string) => setCountryCode(val)}
          setPhone={(val: string) =>
            setFormData((prev) => ({ ...prev, phone: val }))
          }
          email={formData.email || ""}
          setEmail={(val: string) =>
            setFormData((prev) => ({ ...prev, email: val }))
          }
          userPhone={formData?.phone || ""}
          userEmail={formData?.email || ""}
          userCountryCode={formData?.country_code || ""}
          handleSaveProfile={(contact: string, type: "phone" | "email") =>
            handleSaveProfile({ ...formData, [type]: contact })
          }
          showToastMessage={showToastMessage}
          mode="phone"
        />
      )}
      {showProfilePicChange && (
        <ActionButtons
          isOpen={showProfilePicChange}
          onClose={() => setShowProfilePicChange(false)}
          title={formData.avatar ? "Change Profile Picture" : "Add Profile Picture"}
          message=""
          actionButtons={[
            // {
            //   text: "Choose Avatar",
            //   handler: () => {
            //     setShowAvatarSelector(true);
            //   },
            // },
            {
              text: "Upload Photo",
              isPrimary: true,
              action: () => {
                setShowProfilePicChange(false);
                router.push("/settings/change-profile-pic");
              },
            },
            // Only show "Remove Photo" if user has an avatar
            ...(formData.avatar
              ? [
                {
                  text: "Remove Photo",
                  action: () => {
                    setShowProfilePicChange(false);
                    handleSaveProfile({ avatar: "" });
                    handleAvatarSelect("");
                  },
                },
              ]
              : []),
            {
              text: "Cancel",
              action: () => {
                setShowProfilePicChange(false);
              },
            },
          ]}
        />
      )}
      <IonToast
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message={toastMessage}
        duration={2000}
        position="top"
      />
    </IonPage>
  );
};

export default MyProfilePage;